import React, { useCallback, useEffect, useState } from 'react';

import {
  acceptReschedule as apiAccept,
  Booking,
  BookingState,
  cancelBooking as apiCancel,
  confirmBooking as apiConfirm,
  getAvailableWindows,
  getBookings,
  rescheduleBooking as apiReschedule,
  requestBooking as apiRequest,
  AvailableWindow,
} from '../api/booking';
import { closingLabel } from '../vocabulary/learningVocabulary';

interface SchedulePanelProps {
  enrollmentId: string;
  enrollmentState: string;
  isParent: boolean;
  isTeacher: boolean;
}

type PickerMode = 'request' | 'reschedule';
interface PickerState {
  booking: Booking;
  mode: PickerMode;
}

const STATE_LABELS: Record<BookingState, string> = {
  Requested: 'Por agendar',
  Confirmed: 'Confirmada',
  Rescheduled: 'Reprogramacion pendiente',
  Cancelled: 'Cancelada',
  Completed: 'Completada',
  NoShow: 'Inasistencia',
};

const STATE_COLORS: Record<BookingState, string> = {
  Requested: '#868e96',
  Confirmed: '#2b8a3e',
  Rescheduled: '#e8590c',
  Cancelled: '#c92a2a',
  Completed: '#1971c2',
  NoShow: '#c92a2a',
};

const ymd = (d: Date) => d.toISOString().slice(0, 10);

const fmt = (iso?: string): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('es-MX', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const slotTitle = (b: Booking) =>
  b.milestoneSequence === 8 ? closingLabel() : `Sesion ${b.milestoneSequence}`;

const SchedulePanel: React.FC<SchedulePanelProps> = ({
  enrollmentId,
  enrollmentState,
  isParent,
  isTeacher,
}) => {
  const myActor: 'parent' | 'teacher' = isParent ? 'parent' : 'teacher';

  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [picker, setPicker] = useState<PickerState | null>(null);
  const [windows, setWindows] = useState<AvailableWindow[]>([]);
  const [windowsLoading, setWindowsLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getBookings(enrollmentId);
      setBookings([...res.bookings].sort((a, b) => a.milestoneSequence - b.milestoneSequence));
    } catch (err: any) {
      setError(err.message || 'No se pudieron cargar las sesiones');
    } finally {
      setLoading(false);
    }
  }, [enrollmentId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Escape is the keyboard equivalent of clicking the backdrop (S11.2). Without
  // it the only way out of the picker is to find Cancel by tabbing.
  useEffect(() => {
    if (!picker) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !busy) setPicker(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [picker, busy]);

  const run = async (fn: () => Promise<unknown>, okMessage: string) => {
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      await fn();
      setInfo(okMessage);
      setPicker(null);
      await load();
    } catch (err: any) {
      setError(err.message || 'No se pudo completar la accion');
    } finally {
      setBusy(false);
    }
  };

  const openPicker = async (booking: Booking, mode: PickerMode) => {
    setPicker({ booking, mode });
    setWindows([]);
    setWindowsLoading(true);
    setError(null);
    try {
      const from = ymd(new Date());
      const to = ymd(new Date(Date.now() + 21 * 24 * 60 * 60 * 1000));
      const res = await getAvailableWindows(enrollmentId, from, to);
      setWindows(res.windows);
    } catch (err: any) {
      setError(err.message || 'No hay disponibilidad publicada por el profesor');
    } finally {
      setWindowsLoading(false);
    }
  };

  const pickWindow = (w: AvailableWindow) => {
    if (!picker) return;
    const { booking, mode } = picker;
    if (mode === 'request') {
      void run(
        () =>
          apiRequest({
            enrollmentId,
            milestoneSequence: booking.milestoneSequence,
            scheduledStart: w.start,
            scheduledEnd: w.end,
          }),
        `Horario propuesto para ${slotTitle(booking)}.`,
      );
    } else {
      void run(
        () => apiReschedule(booking.bookingId, w.start, w.end),
        `Reprogramacion propuesta para ${slotTitle(booking)}.`,
      );
    }
  };

  const cancelOne = (booking: Booking) => {
    const reason = window.prompt('Razon de la cancelacion (opcional):') || undefined;
    void run(() => apiCancel(booking.bookingId, reason), `${slotTitle(booking)} cancelada.`);
  };

  if (loading) return <p style={{ marginTop: 24 }}>Cargando sesiones...</p>;

  const noneScheduled = bookings.length === 0;

  return (
    <section style={{ marginTop: 32 }}>
      <h2>Agendar sesiones</h2>

      {error && <div style={alertStyle('#ffe3e3', '#ff8787', '#c92a2a')}>{error}</div>}
      {info && <div style={alertStyle('#d3f9d8', '#51cf66', '#2b8a3e')}>{info}</div>}

      {noneScheduled && (
        <p style={{ color: '#666' }}>
          {enrollmentState === 'Active'
            ? 'Aun no hay sesiones generadas para esta inscripcion.'
            : 'Las 8 sesiones se generaran automaticamente cuando la inscripcion este activa.'}
        </p>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {bookings.map((b) => {
          const proposedByOther = b.requestedBy && b.requestedBy !== myActor;
          const rescheduleByOther = b.rescheduleProposedBy && b.rescheduleProposedBy !== myActor;
          return (
            <div key={b.bookingId} style={cardStyle}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 8,
                  flexWrap: 'wrap',
                }}
              >
                <strong>{slotTitle(b)}</strong>
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: STATE_COLORS[b.state],
                  }}
                >
                  {STATE_LABELS[b.state]}
                </span>
              </div>

              <div style={{ fontSize: 14, color: '#444', marginTop: 6 }}>
                {b.state === 'Rescheduled' ? (
                  <>
                    <div>Actual: {fmt(b.priorStart || b.scheduledStart)}</div>
                    <div>
                      Propuesta: <strong>{fmt(b.proposedStart)}</strong> (por{' '}
                      {b.rescheduleProposedBy === 'parent' ? 'el padre' : 'el profesor'})
                    </div>
                  </>
                ) : (
                  <div>{b.scheduledStart ? fmt(b.scheduledStart) : 'Sin horario'}</div>
                )}
              </div>

              <div
                style={{
                  display: 'flex',
                  gap: 8,
                  flexWrap: 'wrap',
                  marginTop: 10,
                }}
              >
                {b.state === 'Requested' && !b.scheduledStart && (
                  <button
                    style={btn(busy)}
                    disabled={busy}
                    onClick={() => openPicker(b, 'request')}
                  >
                    Proponer horario
                  </button>
                )}

                {b.state === 'Requested' && b.scheduledStart && (
                  <>
                    {proposedByOther && (
                      <button
                        style={btn(busy)}
                        disabled={busy}
                        onClick={() =>
                          run(() => apiConfirm(b.bookingId), `${slotTitle(b)} confirmada.`)
                        }
                      >
                        Confirmar
                      </button>
                    )}
                    <button
                      style={btnGhost(busy)}
                      disabled={busy}
                      onClick={() => openPicker(b, 'request')}
                    >
                      Proponer otro horario
                    </button>
                  </>
                )}

                {b.state === 'Confirmed' && (
                  <>
                    <button
                      style={btnGhost(busy)}
                      disabled={busy}
                      onClick={() => openPicker(b, 'reschedule')}
                    >
                      Reprogramar
                    </button>
                    <button style={btnDanger(busy)} disabled={busy} onClick={() => cancelOne(b)}>
                      Cancelar
                    </button>
                  </>
                )}

                {b.state === 'Rescheduled' && (
                  <>
                    {rescheduleByOther && (
                      <button
                        style={btn(busy)}
                        disabled={busy}
                        onClick={() =>
                          run(
                            () => apiAccept(b.bookingId),
                            `Reprogramacion de ${slotTitle(b)} aceptada.`,
                          )
                        }
                      >
                        Aceptar reprogramacion
                      </button>
                    )}
                    <button style={btnDanger(busy)} disabled={busy} onClick={() => cancelOne(b)}>
                      Cancelar
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {picker && (
        // The backdrop is a mouse affordance: clicking outside dismisses. Its
        // keyboard equivalent is Escape, wired above — giving the backdrop a tab
        // stop would add a focusable element that announces nothing.
        <div style={pickerBackdrop} onClick={() => !busy && setPicker(null)} aria-hidden>
          <div
            style={pickerCard}
            role="dialog"
            aria-modal="true"
            aria-label="Elegir horario"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <strong>
                {picker.mode === 'request' ? 'Elegir horario' : 'Reprogramar'} —{' '}
                {slotTitle(picker.booking)}
              </strong>
              <button
                onClick={() => !busy && setPicker(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: 20,
                  cursor: 'pointer',
                  lineHeight: 1,
                }}
                aria-label="Cerrar"
              >
                ×
              </button>
            </div>

            {windowsLoading && <p style={{ marginTop: 12 }}>Cargando disponibilidad...</p>}
            {!windowsLoading && windows.length === 0 && (
              <p style={{ marginTop: 12, color: '#666' }}>
                El profesor no tiene horarios disponibles en las proximas 3 semanas.
              </p>
            )}

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                marginTop: 12,
                maxHeight: 320,
                overflowY: 'auto',
              }}
            >
              {windows.map((w) => (
                <button
                  key={`${w.start}-${w.end}`}
                  disabled={busy}
                  onClick={() => pickWindow(w)}
                  style={{
                    textAlign: 'left',
                    padding: '10px 12px',
                    border: '1px solid #d1d5da',
                    borderRadius: 6,
                    background: '#fff',
                    cursor: busy ? 'not-allowed' : 'pointer',
                    fontSize: 14,
                  }}
                >
                  {fmt(w.start)} – {fmt(w.end)}
                  <span style={{ color: '#868e96', fontSize: 12, display: 'block' }}>
                    {w.timezone}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

const alertStyle = (bg: string, border: string, color: string): React.CSSProperties => ({
  background: bg,
  border: `1px solid ${border}`,
  borderRadius: 8,
  padding: 12,
  margin: '12px 0',
  color,
});

const cardStyle: React.CSSProperties = {
  border: '1px solid #d1d5da',
  borderRadius: 8,
  padding: 14,
};

const btn = (disabled: boolean): React.CSSProperties => ({
  padding: '8px 14px',
  border: 'none',
  borderRadius: 4,
  background: disabled ? '#a0c4e8' : '#1971c2',
  color: '#fff',
  fontSize: 13,
  cursor: disabled ? 'not-allowed' : 'pointer',
});

const btnGhost = (disabled: boolean): React.CSSProperties => ({
  padding: '8px 14px',
  border: '1px solid #1971c2',
  borderRadius: 4,
  background: '#fff',
  color: '#1971c2',
  fontSize: 13,
  cursor: disabled ? 'not-allowed' : 'pointer',
});

const btnDanger = (disabled: boolean): React.CSSProperties => ({
  padding: '8px 14px',
  border: '1px solid #c92a2a',
  borderRadius: 4,
  background: '#fff',
  color: '#c92a2a',
  fontSize: 13,
  cursor: disabled ? 'not-allowed' : 'pointer',
});

const pickerBackdrop: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.4)',
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'center',
  padding: 12,
  zIndex: 1000,
};

const pickerCard: React.CSSProperties = {
  background: '#fff',
  borderRadius: 12,
  padding: 16,
  width: '100%',
  maxWidth: 480,
  maxHeight: '80vh',
  overflowY: 'auto',
};

export default SchedulePanel;
